/* eslint-disable import/prefer-default-export */

import _ from 'lodash';
import moment from 'moment';
import { KEY_CANCEL } from '@/shared/config';
import { handleDownloadData } from '@/entities/ecg/lib/ecgSampling';
import { downloadSelectedEcgData } from './downloadSelectedEcgData';

// `ecgDataMap` mirrors Recoil's `ecgDataMapState`, which is declared `atom<any>` in the
// already-typed Recoil/index.ts — no stronger shape exists to import.
type IEcgDataMap = any;

type IHourlySummary = {
  start: string | number;
  stop: string | number;
  dataRange?: { start: number; stop: number };
  [key: string]: any;
};

/**
 * Sizes the byte payload covering a span of ECG recording. Multiplies the difference between stop and start by the per-second byte rate.
 *
 * @param stop - Span end, in milliseconds.
 * @param start - Span start, in milliseconds.
 * @param bytesPerSecond - Recorded bytes produced per second.
 * @returns The byte count covering the span.
 */
const getSizeData = (stop: number, start: number, bytesPerSecond: number) => {
  const diffTime = +stop - +start;
  const sizeData = diffTime * bytesPerSecond;
  return sizeData;
};

const addDataRanges = ({
  summaries,
  start: startEvent,
  stop: stopEvent,
  samplingFrequency,
  channels,
}: {
  summaries: IHourlySummary[];
  start: number;
  stop: number;
  samplingFrequency: number;
  channels: any[];
}) => {
  const bytesPerSample = 2;
  const samplePerMsSecond = (channels.length * bytesPerSample * samplingFrequency) / 1000;
  const lenHourlySummaries = summaries.length;
  for (let i = 0; i < lenHourlySummaries; i += 1) {
    const hourlySummary = summaries[i];
    const { start: startHs, stop: stopHs } = hourlySummary;
    const _startHs = moment(startHs).valueOf();
    const _stopHs = moment(stopHs).valueOf();
    const sizeData = getSizeData(
      Math.min(_stopHs, stopEvent),
      Math.max(_startHs, startEvent),
      samplePerMsSecond,
    );
    const readPosition =
      i === 0 ? getSizeData(startEvent, Math.min(_startHs, startEvent), samplePerMsSecond) : 0;
    hourlySummary.dataRange = {
      start: Math.ceil(readPosition),
      stop: Math.floor(readPosition + sizeData),
    };
  }
};

const filterHourlySummaryRanges = ({
  summaries,
  start,
  stop,
  samplingFrequency,
  channels,
  willAddDataRanges,
}: {
  summaries: { data: IHourlySummary[] };
  start: number;
  stop: number;
  samplingFrequency: number;
  channels: any[];
  willAddDataRanges?: boolean;
}): IHourlySummary[] => {
  const ranges = summaries.data.filter((summary) =>
    _.every([+summary.stop > +start, +summary.start < +stop]),
  );
  if (willAddDataRanges) {
    addDataRanges({
      summaries: ranges,
      start,
      stop,
      samplingFrequency,
      channels,
    });
  }
  return ranges;
};

/**
 * Deinterleaves a flat ECG sample buffer into one array per channel. Walks the buffer in strides of totalChannel and pushes each sample onto its channel, bailing out when the buffer is missing.
 *
 * @param totalChannel - Number of interleaved channels in the buffer.
 * @param binaryData - Interleaved signed 16-bit ECG samples.
 * @returns One sample array per channel, or undefined when binaryData was absent.
 */
const convertBinaryToECGData = (
  totalChannel: number,
  binaryData: Int16Array,
): number[][] | undefined => {
  if (!binaryData) {
    return undefined;
  }
  const ecgData: number[][] = new Array(totalChannel).fill(null).map((): number[] => []);
  const binaryDataLength = binaryData.length;
  for (let i = 0; i < binaryDataLength; i += totalChannel) {
    for (let j = 0; j < totalChannel; j += 1) {
      ecgData[j].push(binaryData[i + j]);
    }
  }
  return ecgData;
};

/**
 * Builds the placeholder sine wave drawn across gaps in an ECG recording. Generates one second of samples scaled to three quarters of half the ADC gain and offset by 1.25 gain either side of the half period, repeats it for the whole seconds in the gap and appends a proportional slice for the remainder.
 *
 * @param diffMilliseconds - Length of the gap to fill, in milliseconds.
 * @param samplingFrequency - Sampling rate, also the sample count of one wave period.
 * @param adcGain - ADC gain the wave amplitude and offset are scaled to.
 * @returns The sine wave samples covering the gap.
 */
const getSineWaveDataArray = (
  diffMilliseconds: number,
  samplingFrequency: number,
  adcGain: number,
): number[] => {
  const halfSamplingFrequency = samplingFrequency / 2;
  const sineWaveCount = diffMilliseconds / 1000;

  const sineWaveDataArray = _.map(_.range(samplingFrequency), (x, i) => {
    const sample = Math.sin(x * ((2 * Math.PI) / samplingFrequency)) * (0.75 * (adcGain / 2));
    return i <= halfSamplingFrequency
      ? Math.round(sample - 1.25 * adcGain)
      : Math.round(sample + 1.25 * adcGain);
  });
  let resultSineWave: number[] = [];
  const wholeSineWaveCount = Math.floor(sineWaveCount);
  const fractionalSineWaveCount = sineWaveCount - wholeSineWaveCount;
  for (let k = 0; k < wholeSineWaveCount; k += 1) {
    resultSineWave = resultSineWave.concat(sineWaveDataArray);
  }
  if (fractionalSineWaveCount) {
    resultSineWave = resultSineWave.concat(
      sineWaveDataArray.slice(0, Math.round(fractionalSineWaveCount * sineWaveDataArray.length)),
    );
  }
  return resultSineWave;
};

/**
 * Clips the recorded data ranges to a requested window and pins the window edges. Drops ranges left empty after clamping, then prepends a zero-width range at start and appends one at stop while the window is non-empty.
 *
 * @param ranges - Recorded epoch ranges as start and stop pairs.
 * @param start - Window start epoch.
 * @param stop - Window stop epoch.
 * @returns The clipped ranges bracketed by zero-width start and stop markers.
 */
const combineRanges = (ranges: number[][], start: number, stop: number): number[][] => {
  const adjustedRanges: (number[] | null)[] = ranges
    .map((subarray) => {
      let [subStart, subStop] = subarray;
      subStart = Math.max(subStart, start);
      subStop = Math.min(subStop, stop);
      return subStart <= subStop ? [subStart, subStop] : null;
    })
    .filter((subarray) => subarray !== null);

  const result = adjustedRanges as number[][];
  if (start <= stop) {
    result.unshift([start, start]);
    result.push([stop, stop]);
  }

  return result;
};

/**
 * Splices placeholder sine waves into downloaded ECG so gaps in the recording keep their real duration. Returns the input untouched when it already spans dataLength, otherwise clips the hourly-summary ranges to the window and walks them, pushing a generated wave for each gap and the matching slice of real samples for each covered span, both in fifty-thousand-sample chunks to spare the call stack.
 *
 * @param selectedEcgData - Downloaded samples, one array per channel.
 * @param ranges - Hourly summary ranges covering the window.
 * @param startMoment - Window start epoch.
 * @param stopMoment - Window stop epoch.
 * @param ecgDataMap - ECG map supplying samplingFrequency and gain.
 * @param dataLength - Expected sample count per channel; a match short-circuits the work.
 * @returns The per-channel samples with every gap filled by a sine wave.
 */
const addSineWaveToEcgData = (
  selectedEcgData: number[][],
  ranges: IHourlySummary[],
  startMoment: number,
  stopMoment: number,
  ecgDataMap: IEcgDataMap,
  dataLength = 75000,
): number[][] => {
  if (selectedEcgData[0].length === dataLength) {
    return selectedEcgData;
  }
  const { samplingFrequency, gain } = ecgDataMap;
  const ecgData: number[][] = _.map(selectedEcgData, (): number[] => []);
  const rangesEpochs = ranges.map((x) => [moment(x.start).valueOf(), moment(x.stop).valueOf()]);
  const startEpoch = moment(startMoment).valueOf();
  const stopEpoch = moment(stopMoment).valueOf();
  const rangesData = combineRanges(rangesEpochs, startEpoch, stopEpoch);
  let dataIndex = 0;
  const chunk = 50000;
  rangesData.forEach((range, i) => {
    if (i > 0) {
      const currStart = range[0];
      const currStop = range[1];
      const prevStop = rangesData[i - 1][1];
      if (currStart - prevStop > 0) {
        const sineWave = getSineWaveDataArray(currStart - prevStop, samplingFrequency, gain);
        ecgData.forEach((data) => {
          const { length } = sineWave;
          for (let i = 0; i < length; i += chunk) {
            // need chunk to avoid stack size exceeded
            data.push(...sineWave.slice(i, i + chunk));
          }
        });
      }
      if (currStop - currStart > 0) {
        const diffMs = (currStop - currStart) / 1000;
        const rangeDataLength = Math.floor(diffMs * samplingFrequency);
        _.forEach(ecgData, (data, j) => {
          const { length } = selectedEcgData[j].slice(dataIndex, dataIndex + rangeDataLength);
          for (let i = 0; i < length; i += chunk) {
            data.push(
              ...selectedEcgData[j]
                .slice(dataIndex, dataIndex + rangeDataLength)
                .slice(i, i + chunk),
            );
          }
        });
        dataIndex += rangeDataLength;
      }
    }
  });
  return ecgData;
};

/**
 * Produces the display-ready ECG samples for a window, falling back to the online download when the cached path fails. Validates the arguments and the map's channels and samplingFrequency, resolves the hourly-summary ranges, downloads the byte ranges, then deinterleaves them and splices sine waves into the gaps; a download error or an empty response defers to handleDownloadData.
 *
 * @param startMoment - Window start epoch.
 * @param stopMoment - Window stop epoch.
 * @param ecgDataMap - ECG map cloned before use, supplying channels, samplingFrequency and querySignature.
 * @param dataLength - Expected sample count per channel.
 * @param propsData - Original request context forwarded to handleDownloadData on the fallback path.
 * @returns A promise resolving to the per-channel samples for the window.
 */
const processEcgData = async ({
  startMoment,
  stopMoment,
  ecgDataMap,
  dataLength,
  propsData = {} as {
    beat?: { id?: number; start: string | number };
    offset?: number;
    ecgDataMap?: IEcgDataMap;
    dataLength?: number;
    momentObject?: { startMoment?: string | number; stopMoment?: string | number };
    config?: { customOffset?: [number, number] };
  },
}: {
  startMoment: number;
  stopMoment: number;
  ecgDataMap: IEcgDataMap;
  dataLength: number;
  propsData?: {
    beat?: { id?: number; start: string | number };
    offset?: number;
    ecgDataMap?: IEcgDataMap;
    dataLength?: number;
    momentObject?: { startMoment?: string | number; stopMoment?: string | number };
    config?: { customOffset?: [number, number] };
  };
}) => {
  const { beat, offset, momentObject, config } = propsData;
  // Kiểm tra các tham số đầu vào
  if (!startMoment || !stopMoment || !ecgDataMap || !dataLength) {
    throw new Error('Missing required parameters.');
  }

  const { channels, samplingFrequency } = ecgDataMap;
  if (!channels || !samplingFrequency) {
    throw new Error('Invalid ecgDataMap structure.');
  }

  // Tạo mãng mới tránh việc thay đổi dữ liệu gốc
  const _ecgDataMap = _.cloneDeep(ecgDataMap);

  // Lấy ra các khoảng thời gian để xử lý dữ liệu ECG
  let ranges: IHourlySummary[];
  try {
    ranges = filterHourlySummaryRanges({
      summaries: _ecgDataMap,
      start: startMoment,
      stop: stopMoment,
      samplingFrequency,
      channels,
      willAddDataRanges: true,
    });
  } catch (rangeError) {
    console.error('Error calculating ranges:', rangeError);
    throw rangeError; // Hoặc xử lý lỗi phù hợp
  }

  // Tải về dữ liệu ECG đã chọn từ DB
  let downloadedSelectedEcgData: Int16Array | any[] = [];
  try {
    if (downloadedSelectedEcgData.length === 0) {
      downloadedSelectedEcgData = await downloadSelectedEcgData(
        ranges,
        _ecgDataMap.querySignature,
        { cancelKey: KEY_CANCEL.DOWLOAD_ECG },
      );
    }
  } catch (downloadError) {
    console.error('Error downloading selected ECG data:', downloadError);
    const sineWaveDataOnline = await handleDownloadData({
      beat,
      offset,
      ecgDataMap,
      dataLength,
      momentObject,
      config,
    });
    return sineWaveDataOnline;
  }

  // Xử lý dữ liệu nếu có
  if (downloadedSelectedEcgData?.length > 0) {
    try {
      const selectedEcgData = convertBinaryToECGData(
        channels.length,
        downloadedSelectedEcgData as Int16Array,
      );
      const sineWaveData = addSineWaveToEcgData(
        selectedEcgData!,
        ranges,
        startMoment,
        stopMoment,
        ecgDataMap,
        dataLength,
      );
      return sineWaveData;
    } catch (processError) {
      console.error('Error processing ECG data:', processError);
      throw processError; // Hoặc xử lý lỗi phù hợp
    }
  } else {
    // Nếu không có dữ liệu được tải về, xử lý thông qua handleDownloadData
    const sineWaveDataOnline = await handleDownloadData({
      beat,
      offset,
      ecgDataMap,
      dataLength,
      momentObject,
      config,
    });
    return sineWaveDataOnline;
  }
};

// =================Handler functions=========================

/**
 * Resolves the ECG window for a beat or an explicit moment pair and loads its samples. Uses momentObject when present, otherwise floors the beat epoch onto the sampling grid and widens it by customOffset or offset, then delegates to processEcgData, returning null when anything throws.
 *
 * @param beat - Beat anchoring the window; its id, or its start parsed as a date, supplies the epoch.
 * @param offset - Symmetric half-width in milliseconds around the beat epoch.
 * @param ecgDataMap - ECG map supplying samplingFrequency and the download metadata.
 * @param dataLength - Expected sample count per channel.
 * @param momentObject - Explicit startMoment and stopMoment overriding the beat-derived window.
 * @param config - Optional customOffset pair giving asymmetric widths before and after the epoch.
 * @returns A promise resolving to the per-channel samples, or null when the load failed.
 */
const checkAndProcessEcgData = async ({
  beat,
  offset,
  ecgDataMap,
  dataLength,
  momentObject = {} as { startMoment?: string | number; stopMoment?: string | number },
  config = {} as { customOffset?: [number, number] },
}: {
  beat?: { id?: number; start: string | number };
  offset?: number;
  ecgDataMap: IEcgDataMap;
  dataLength: number;
  momentObject?: { startMoment?: string | number; stopMoment?: string | number };
  config?: { customOffset?: [number, number] };
}) => {
  try {
    const { customOffset } = config || {};
    const { samplingFrequency } = ecgDataMap;

    // pre-existing: startMoment/stopMoment stay unassigned (and the `!` below lies)
    // when momentObject, customOffset and offset are all absent.
    let startMoment: number;
    let stopMoment: number;
    if (!_.isEmpty(momentObject)) {
      // *: Draw selected event
      startMoment = moment(momentObject.startMoment).valueOf();
      stopMoment = moment(momentObject.stopMoment).valueOf();
    } else {
      let epoch = beat?.id ? beat?.id : new Date(beat!.start).getTime();
      const dividedNumber = 1000 / samplingFrequency;
      epoch = Math.floor(epoch / dividedNumber) * dividedNumber;
      if (!_.isEmpty(customOffset)) {
        startMoment = moment(epoch - customOffset![0]).valueOf();
        stopMoment = moment(epoch + customOffset![1]).valueOf();
      } else if (offset) {
        startMoment = moment(epoch - offset).valueOf();
        stopMoment = moment(epoch + offset).valueOf();
      }
    }

    return await processEcgData({
      startMoment: startMoment!,
      stopMoment: stopMoment!,
      ecgDataMap,
      dataLength,
      propsData: {
        beat,
        offset,
        ecgDataMap,
        dataLength,
        momentObject,
        config,
      },
    });
  } catch (error) {
    console.error('Error in handlerEcgData:', error);
    return null; // Ensure a value is always returned
  }
};

export { checkAndProcessEcgData };
